import { Query, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@zorro/clients';
import { responseErrorToString } from '@zorro/common-utils';
import { ResultAsync } from 'neverthrow';

import {
  MonolithMethodNames,
  MonolithMethods,
  callEndpoint,
} from '../callEndpoint';
import {
  showErrorNotification,
  showSuccessNotification,
} from '../notifications';
import { useDynamicRouter } from './useDynamicRouter';
import { useLoadingOverlay } from './useLoadingOverlay';

type UseMonolithMutationProps<T extends MonolithMethodNames> = {
  method: T;
  anonymous?: boolean;
  successMessage?: string;
  shouldShowLoadingOverlay?: boolean;
  shouldInvalidateQueries?: boolean;
  shouldReloadPage?: boolean;
  displayErrorNotification?: boolean;
  excludeInvalidateQueries?: MonolithMethodNames[];
};

type MutationOptions = {
  flowId?: string;
};

type MutationVariables<T extends MonolithMethodNames> = {
  params: Parameters<MonolithMethods[T]>;
} & MutationOptions;

type UseMonolithMutationReturnType<T extends MonolithMethodNames> = {
  mutate: (
    params: Parameters<MonolithMethods[T]>,
    options?: MutationOptions
  ) => Promise<ReturnType<MonolithMethods[T]>>;
  tryMutate: (
    params: Parameters<MonolithMethods[T]>,
    options?: MutationOptions
  ) => ResultAsync<Awaited<ReturnType<MonolithMethods[T]>>, ApiError>;
  data?: Awaited<ReturnType<MonolithMethods[T]>>;
  isPending: boolean;
  status: 'pending' | 'error' | 'success' | 'idle';
};

export function useMonolithMutation<T extends MonolithMethodNames>({
  method,
  anonymous,
  successMessage,
  shouldReloadPage = true,
  shouldInvalidateQueries = true,
  shouldShowLoadingOverlay = true,
  displayErrorNotification = true,
  excludeInvalidateQueries = [],
}: UseMonolithMutationProps<T>): UseMonolithMutationReturnType<T> {
  const { startLoading, stopLoading } = useLoadingOverlay();
  const { reloadPage } = useDynamicRouter({});
  const queryClient = useQueryClient();

  const { mutateAsync, status, isPending, data } = useMutation<
    ReturnType<MonolithMethods[T]>,
    ApiError,
    MutationVariables<T>
  >({
    mutationFn: ({ params, flowId }) => {
      if (shouldShowLoadingOverlay) {
        startLoading();
      }
      return callEndpoint({
        method,
        anonymous,
        params,
        flowId,
      });
    },
    onSuccess: async () => {
      if (successMessage) {
        showSuccessNotification({ message: successMessage });
      }
      if (shouldInvalidateQueries) {
        const filters =
          excludeInvalidateQueries?.length > 0
            ? {
                predicate: (query: Query) => {
                  const methodName = query.queryKey[0] as MonolithMethodNames;
                  return !excludeInvalidateQueries?.includes(methodName);
                },
              }
            : undefined;
        setTimeout(async () => {
          await queryClient.invalidateQueries(filters);
        }, 0);
      }
      if (shouldReloadPage) {
        reloadPage();
      }
    },
    onError: (error) => {
      if (displayErrorNotification) {
        showErrorNotification({ message: responseErrorToString(error) });
      }
    },
    onSettled: () => {
      if (shouldShowLoadingOverlay) {
        stopLoading();
      }
    },
  });

  const mutate = (
    params: Parameters<MonolithMethods[T]>,
    { flowId }: MutationOptions = {}
  ) => mutateAsync({ params, flowId });

  return {
    mutate,
    tryMutate: ResultAsync.fromThrowable(mutate),
    isPending,
    status,
    data,
  } as UseMonolithMutationReturnType<T>;
}
